<template>
  <div class="modal-overlay z900 announcement-backdrop" @click="$emit('dismiss')">
    <div v-if="hasIn" class="confetti-layer" aria-hidden="true">
      <span v-for="p in confetti" :key="p.id"
            class="confetti-piece"
            :style="{
              background: p.color,
              width: p.size + 'px',
              height: (p.size * 0.4) + 'px',
              animationDelay: p.delay + 'ms',
              '--dx': p.dx + 'px',
              '--dy': p.dy + 'px',
              '--rot': p.rot + 'deg',
            }"></span>
    </div>
    <div class="announcement-panel" role="dialog" aria-modal="true" aria-label="Tournament announcement">
      <div v-if="skillDrop" class="announcement-drop" :class="'rarity-' + skillDrop.rarity">
        <span class="ad-label">🎁 Skill Drop</span>
        <span class="ad-name">{{ skillDrop.name }}</span>
        <span class="ad-rarity">{{ skillDrop.rarity }}</span>
      </div>
      <div v-for="(m, i) in matches" :key="i" class="announcement-item">
        <p class="announcement-line" :style="m.color ? { color: m.color } : null">
          <a v-if="m.status === 'in'"
             href="#"
             class="player-link"
             :style="m.color ? { color: m.color } : null"
             @click.stop.prevent="$emit('scroll-to-player', m.name)">{{ m.name }}</a><template v-else>{{ m.name }}</template>
          {{ m.status === 'in' ? 'is in!' : 'was Snubbed.' }}
        </p>
        <div v-if="m.status === 'in'" class="announcement-units">
          <div v-for="(uu, j) in unitsFor(m)" :key="j" class="announcement-unit">
            <img class="au-portrait" :src="portrait(uu.unit, uu.teamLabel)" :alt="uu.unit.Class" @error="$event.target.style.visibility = 'hidden'">
            <div class="au-info">
              <div class="au-class">{{ uu.unit.Class }}<span v-if="uu.teamLabel" class="au-team"> · {{ uu.teamLabel === 'Champion' ? 'Champion (defending)' : uu.teamLabel + ' team' }}</span></div>
              <div v-if="uu.grade" class="au-grade"><span class="grade-badge" :class="'tier-' + uu.grade.color" title="Color = class tier · letter = this unit within its class">{{ uu.grade.grade }}</span><span class="au-grade-score">~{{ uu.grade.score }}% expected win rate</span></div>
              <div v-if="unitSkills(uu.unit)" class="au-line">{{ unitSkills(uu.unit) }}</div>
              <div v-if="unitGear(uu.unit)" class="au-line au-gear">{{ unitGear(uu.unit) }}</div>
            </div>
          </div>
        </div>
      </div>
      <p v-if="settingsHint" class="settings-hint">
        💡 Luxor &amp; friends are highlighted by default — that's why you're seeing this.
        <a v-if="settingsLink" href="#" class="settings-link" @click.stop.prevent="$emit('open-settings')">⚙ Settings</a><template v-else>The ⚙ Settings on the bracket page</template>
        lets you highlight your own players instead — or turn these popups off.
      </p>
      <p class="dismiss-hint">click anywhere to dismiss</p>
    </div>
  </div>
</template>

<script>
const CONFETTI_COLORS = ['#ff3b3b', '#ffd700', '#00e676', '#1e90ff', '#ff69b4', '#ffffff', '#ff8c00'];
import { UNITS_BASE as UNIT_IMG } from '../lib/assetUrls.js';
import modalA11y from '../mixins/modalA11y.js';

export default {
  mixins: [modalA11y],
  closeEvent: 'dismiss',
  props: {
    // [{ name, color, status: 'in'|'snubbed', units? }] — pre-built by parent.
    // 'in' entries carry a `units` array (a player can field a new team AND be the
    // defending champion → both are shown). Legacy single `unit`/`teamLabel`/`grade`
    // is still accepted via unitsFor().
    matches: { type: Array, required: true },
    // { token, name, category, rarity } for the tournament's !buyrandom drop, or null.
    skillDrop: { type: Object, default: null },
    // Show the "these are the default highlights — see ⚙ Settings" footer
    // (parents pass true only for fresh visitors with untouched prefs).
    settingsHint: { type: Boolean, default: false },
    // Whether "⚙ Settings" is clickable (emits open-settings). True on the
    // bracket page, false on sub-pages where the gear modal doesn't exist.
    settingsLink: { type: Boolean, default: false },
  },
  emits: ['dismiss', 'scroll-to-player', 'open-settings'],

  data() {
    // Pre-compute particle positions in JS so each piece gets a unique
    // CSS-variable-driven trajectory. ~60 feels celebratory without lagging.
    const confetti = Array.from({ length: 60 }, (_, i) => {
      // Angle weighted upward so the burst goes up-and-out, then gravity pulls down.
      const angle = Math.PI + Math.random() * Math.PI;
      const distance = 120 + Math.random() * 260;
      return {
        id: i,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        size: 6 + Math.random() * 8,
        dx: Math.cos(angle) * distance,
        dy: Math.sin(angle) * distance + 320,
        rot: (Math.random() * 1440) - 720,
        delay: Math.random() * 250,
      };
    });
    return { confetti };
  },

  computed: {
    hasIn() {
      return this.matches.some(m => m.status === 'in');
    },
  },
  methods: {
    // The unit(s) to show for a match — a `units` array if present, else the legacy
    // single unit. A player who entered AND is the defending champion has two.
    unitsFor(m) {
      if (Array.isArray(m.units)) return m.units.filter((x) => x && x.unit);
      return m.unit ? [{ unit: m.unit, teamLabel: m.teamLabel, grade: m.grade }] : [];
    },
    // Team-coloured portrait (same scheme as the bracket); monsters/champion plain.
    portrait(u, label) {
      if (u.Gender === 'Monster') return UNIT_IMG + u.Class + '.gif';
      const g = (u.Gender || 'Male')[0];
      if (label === 'Champion') return UNIT_IMG + u.Class + g + '.gif';
      return UNIT_IMG + u.Class + g + '_' + label + '.gif';
    },
    unitSkills(u) { return [u.ActionSkill, u.ReactionSkill, u.SupportSkill, u.MoveSkill].filter(Boolean).join(' · '); },
    unitGear(u) { return [u.Mainhand, u.Offhand, u.Head, u.Armor, u.Accessory].filter(Boolean).join(', '); },
  },
};
</script>

<style scoped>
.announcement-backdrop {
  background: rgba(0, 0, 0, 0.6);
  cursor: pointer;
  padding: 24px;
  overflow: hidden;
}
.announcement-panel {
  position: relative;
  z-index: 2;
  /* the 2026-10 refresh's brass-framed navy panel (assets/css/theme.scss) */
  background:
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 top 0 / 14px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 top 0 / 2px 14px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 top 0 / 14px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 top 0 / 2px 14px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 bottom 0 / 14px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 bottom 0 / 2px 14px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 bottom 0 / 14px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 bottom 0 / 2px 14px no-repeat,
    linear-gradient(180deg, var(--panel-top), var(--panel-bottom));
  border: 1px solid var(--brass-edge);
  border-radius: 6px;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.6);
  color: var(--ink);
  padding: 28px 44px 20px;
  text-align: center;
  max-width: 90vw;
}
.announcement-line {
  font-family: var(--display);
  font-weight: 700;
  font-size: 32px;
  color: var(--ink-hi);
  text-shadow: 0 2px 0 rgba(0, 0, 0, 0.5);
  margin: 8px 0;
  line-height: 1.15;
}
@media (max-width: 600px) {
  .announcement-line { font-size: 24px; }
}
.player-link {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 4px;
  cursor: pointer;
}
.player-link:hover { filter: brightness(1.25); }

/* Skill-drop banner — rarity colors mirror the FFTBG "User Skills" sheet. */
.announcement-drop {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0 auto 18px;
  padding: 8px 16px;
  border: 1px solid var(--brass-edge);
  border-top: 3px solid #8b93a3;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.04);
}
.announcement-drop .ad-label { font-family: var(--cond); font-weight: 700; font-size: 13px; text-transform: uppercase; letter-spacing: 1.6px; color: var(--brass); }
.announcement-drop .ad-name { font-family: var(--cond); font-size: 21px; font-weight: 700; color: var(--ink-hi); }
.announcement-drop .ad-rarity { font-size: 12px; text-transform: capitalize; opacity: 0.85; }
.announcement-drop.rarity-uncommon { border-top-color: #6aa84f; }
.announcement-drop.rarity-rare     { border-top-color: #4f8fe0; }

.announcement-item { margin-bottom: 14px; }
.announcement-units { display: flex; flex-direction: column; align-items: center; gap: 8px; margin-top: 4px; }
.announcement-unit { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 6px 12px; border: 1px solid var(--line); border-radius: 4px; background: rgba(0, 0, 0, 0.18); }
.au-portrait { width: 52px; height: 52px; object-fit: contain; image-rendering: pixelated; flex: 0 0 auto; }
.au-info { text-align: left; max-width: 380px; }
.au-class { font-family: var(--cond); font-size: 20px; font-weight: 700; color: var(--ink-hi); }
.au-grade { font-size: 13px; margin: 3px 0; display: flex; align-items: center; gap: 7px; }
.au-grade-score { opacity: 0.85; }
.au-team { font-weight: normal; opacity: 0.8; font-size: 14px; }
.au-line { font-size: 12px; opacity: 0.85; line-height: 1.4; }
.au-gear { opacity: 0.62; }

.settings-hint {
  margin: 22px auto 0;
  max-width: 460px;
  padding-top: 12px;
  border-top: 1px solid var(--line);
  font-size: 12.5px;
  line-height: 1.5;
  opacity: 0.85;
}
.settings-hint .settings-link {
  color: var(--brass-hi);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
}
.settings-hint + .dismiss-hint { margin-top: 8px; }

.dismiss-hint {
  margin-top: 18px;
  font-family: var(--cond);
  font-size: 13px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--mute);
}

.confetti-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
}
.confetti-piece {
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 1px;
  opacity: 0;
  animation: confetti-burst 1800ms cubic-bezier(.15,.7,.4,1) forwards;
}
@keyframes confetti-burst {
  0%   { transform: translate(-50%, -50%) rotate(0deg);                       opacity: 1; }
  10%  { opacity: 1; }
  100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--rot)); opacity: 0; }
}
</style>
